import { ChatroomKbn } from 'common/constant';
import { ResponseGenerator } from 'common/type';
import { trimQueryData } from 'helpers/params';
import { deleteChatroomAPI, getChatroomByKindAPI } from 'modules/chatroom/api';
import { call, put, takeLatest } from 'redux-saga/effects';

import { upsertChatroomAndMember } from '../chatroom/saga';

import {
  getCustomsClearanceRequestAPI,
  getCustomsClearanceRequestDetailAPI,
  getPicAPI,
  addCustomsClearanceRequestAPI,
  updateCustomsClearanceRequestAPI,
  deleteCustomsClearanceRequestAPI,
  getHolidayAPI,
  uploadOutsideDocAPI,
  getCustomsClearanceRequestCalendarAPI,
  foldFlagHblDisplayCalendarFlgAPI,
} from './api';
import {
  filterCustomsClearanceRequest,
  filterCustomsClearanceRequestSuccess,
  filterCustomsClearanceRequestFailure,
  getDetailCustClearReq,
  getDetailCustClearReqSuccess,
  getPic,
  getPicSuccess,
  getPicFailure,
  getCnee,
  getCneeSuccess,
  getCneeFailure,
  getShippingInstruction,
  getShippingInstructionSuccess,
  getShippingInstructionFailure,
  registerCustomsClearanceRequest,
  registerCustomsClearanceRequestSuccess,
  registerCustomsClearanceRequestFailure,
  updateCustomsClearanceRequest,
  updateCustomsClearanceRequestSuccess,
  updateCustomsClearanceRequestFailure,
  deleteCustomsClearanceRequest,
  deleteCustomsClearanceRequestSuccess,
  deleteCustomsClearanceRequestFailure,
  getHoliday,
  getHolidaySuccess,
  getHolidayFailure,
  uploadOutsideDoc,
  uploadOutsideDocSuccess,
  uploadOutsideDocFailure,
  filterCustomsClearanceRequestCalendar,
  filterCustomsClearanceRequestCalendarSuccess,
  filterCustomsClearanceRequestCalendarFailure,
  foldFlagHblDisplayCalendarFlg,
  foldFlagHblDisplayCalendarFlgSuccess,
  foldFlagHblDisplayCalendarFlgFailure,
} from './slice';

function* fetchCustomsClearanceRequest({ payload }: any) {
  try {
    const response: ResponseGenerator = yield call(getCustomsClearanceRequestAPI, trimQueryData(payload));
    yield put(filterCustomsClearanceRequestSuccess(response));
  } catch (error) {
    yield put(filterCustomsClearanceRequestFailure());
  }
}

function* fetchCustomsClearanceRequestDetail({ payload }: any) {
  const { onSuccess, onFail, ...data } = payload;

  try {
    const response: ResponseGenerator = yield call(getCustomsClearanceRequestDetailAPI, { ...data });
    if (onSuccess) {
      onSuccess(response);
      return;
    }
    yield put(getDetailCustClearReqSuccess(response));
  } catch (error) {
    onFail();
  }
}

function* fetchPic({ payload }: any) {
  try {
    const response: ResponseGenerator = yield call(getPicAPI, trimQueryData(payload));
    yield put(getPicSuccess(response));
  } catch (error) {
    yield put(getPicFailure());
  }
}

function* fetchCnee({ payload }: any) {
  try {
    const response: ResponseGenerator = yield call(getPicAPI, trimQueryData(payload));
    yield put(getCneeSuccess(response));
  } catch (error) {
    yield put(getCneeFailure());
  }
}

function* fetchShippingInstruction({ payload }: any) {
  try {
    const response: ResponseGenerator = yield call(getPicAPI, trimQueryData(payload));
    yield put(getShippingInstructionSuccess(response));
  } catch (error) {
    yield put(getShippingInstructionFailure());
  }
}

function setMultipartFormData(payload: any) {
  const convertObjToFormData = (
    formData: FormData,
    key: string,
    obj: any,
    isArray: boolean = false,
    index?: number
  ) => {
    Object.keys(obj).forEach((childValueKey) => {
      const childValue = obj[childValueKey];
      if (!childValue) return;
      formData.append(`${key}${isArray ? `[${index}]` : ''}[${childValueKey}]`, childValue);
    });
  };

  const formData = new FormData();
  Object.keys(payload).forEach((key) => {
    const value = payload[key];
    if (!value) return;
    if (Array.isArray(value)) {
      value.forEach((v: any, i: number) => {
        // 配列の要素はObjectかつ1階層の前提
        convertObjToFormData(formData, key, v, true, i);
      });
    } else if (typeof value === 'object' && value !== null) {
      convertObjToFormData(formData, key, value);
    } else {
      formData.append(key, value);
    }
  });
  return formData;
}

function* registerCustomsClearanceRequestSaga({ payload }: any) {
  try {
    const formData = setMultipartFormData(payload);
    const addResponse: ResponseGenerator = yield call(addCustomsClearanceRequestAPI, formData);

    const [picUsers, cneeUsers] = comvertPicAndCneeUserId(payload.PIC, payload.CNEE);

    // チャットルーム作成
    yield upsertChatroomAndMember({
      targets: [
        // {
        //   kind: ChatroomKbn.CCR_IN_HOUSE_EXPORT,
        //   addUsers: picUsers,
        // },
        {
          kind: ChatroomKbn.CCR_CLIENT_EXPORT,
          addUsers: picUsers.concat(cneeUsers),
        },
      ],
      key: { KEY: addResponse.data.CUST_CLEAR_REQ_NO },
      executeUserId: payload.EXECUTING_USER_ID,
    });

    // 登録完了メッセージを設定
    if (payload.DRAFT_FLG === '1') {
      addResponse.message = 'message.customs_clearance_request.0007';
    } else {
      addResponse.message = 'message.customs_clearance_request.0008';
    }

    yield put(registerCustomsClearanceRequestSuccess(addResponse));
  } catch (error: any) {
    yield put(registerCustomsClearanceRequestFailure({ message: error?.message }));
  }
}

function* updateCustomsClearanceRequestSaga({ payload }: any) {
  try {
    const formData = setMultipartFormData(payload);
    const updateResponse: ResponseGenerator = yield call(updateCustomsClearanceRequestAPI, formData);

    const [addPicUsers, addCneeUsers] = comvertPicAndCneeUserId(payload.PIC, payload.CNEE);
    const addAllUsers = addPicUsers.concat(addCneeUsers);
    const [delPicUsers, delCneeUsers] = comvertPicAndCneeUserId(payload.PIC_DEL, payload.CNEE_DEL);
    const delAllUsers = delPicUsers.concat(delCneeUsers);

    // チャットルーム更新
    yield upsertChatroomAndMember({
      targets: [
        // {
        //   kind: ChatroomKbn.CCR_IN_HOUSE_EXPORT,
        //   addUsers: addPicUsers,
        //   deleteUsers: delPicUsers,
        // },
        {
          kind: ChatroomKbn.CCR_CLIENT_EXPORT,
          addUsers: addAllUsers,
          deleteUsers: delAllUsers,
        },
      ],
      key: { KEY: payload.CUST_CLEAR_REQ_NO },
      executeUserId: payload.EXECUTING_USER_ID,
    });

    // 更新完了メッセージを設定
    updateResponse.message = 'message.customs_clearance_request.0009';

    yield put(updateCustomsClearanceRequestSuccess(updateResponse));
  } catch (error: any) {
    yield put(updateCustomsClearanceRequestFailure({ message: error?.message }));
  }
}

function* deleteCustomsClearanceRequestSaga({ payload }: any) {
  try {
    const deleteCustomsClearanceResponse: ResponseGenerator = yield call(deleteCustomsClearanceRequestAPI, {
      CUST_CLEAR_REQ_NO: payload.CUST_CLEAR_REQ_NO,
      UPD_DT: payload.UPD_DT,
    });

    // 通関依頼チャットルームの削除のため、対象の通関依頼チャットルームがあれば取得する
    const roomSearchResponse: ResponseGenerator = yield call(getChatroomByKindAPI, {
      params: {
        KEY: payload.CUST_CLEAR_REQ_NO,
        EXECUTING_USER_ID: payload.EXECUTING_USER_ID,
      },
    });
    const serachDatas = roomSearchResponse.data?.GetChatroomByKind?.items || [];

    for (const serachData of serachDatas) {
      if (serachData.KIND === ChatroomKbn.CCR_IN_HOUSE_EXPORT || serachData.KIND === ChatroomKbn.CCR_CLIENT_EXPORT) {
        // 通関依頼チャットルームがある場合論理削除
        yield call(deleteChatroomAPI, {
          params: {
            CHATROOM_ID: serachData.CHATROOM_ID,
            EXECUTING_USER_ID: payload.EXECUTING_USER_ID,
          },
        });
      }
    }

    yield put(deleteCustomsClearanceRequestSuccess(deleteCustomsClearanceResponse));
  } catch (error: any) {
    yield put(deleteCustomsClearanceRequestFailure({ message: error?.message }));
  }
}

function* fetchHoliday({ payload }: any) {
  try {
    const response: ResponseGenerator = yield call(getHolidayAPI, trimQueryData(payload));
    yield put(getHolidaySuccess(response));
  } catch (error) {
    yield put(getHolidayFailure());
  }
}

function* uploadOutsideDocSaga({ payload }: any) {
  try {
    const formData = setMultipartFormData(payload);
    const addResponse: ResponseGenerator = yield call(uploadOutsideDocAPI, formData);

    yield put(uploadOutsideDocSuccess(addResponse));
  } catch (error: any) {
    yield put(uploadOutsideDocFailure({ message: error?.message }));
  }
}

function* fetchCustomsClearanceCalendarRequest({ payload }: any) {
  try {
    const response: ResponseGenerator = yield call(getCustomsClearanceRequestCalendarAPI, payload);
    yield put(filterCustomsClearanceRequestCalendarSuccess(response));
  } catch (error) {
    yield put(filterCustomsClearanceRequestCalendarFailure());
  }
}

function* fetchFoldFlagHblDisplayCalendarFlg({ payload }: any) {
  try {
    const response: ResponseGenerator = yield call(foldFlagHblDisplayCalendarFlgAPI, trimQueryData(payload));
    yield put(foldFlagHblDisplayCalendarFlgSuccess(response));
  } catch (error) {
    yield put(foldFlagHblDisplayCalendarFlgFailure());
  }
}

const comvertPicAndCneeUserId = (picDatas: any[], cneeDatas: any[]) => {
  const comvertUserIdField = (targetDatas: any[], alias: string) => {
    return targetDatas.map((t: any) => {
      return { USER_ID: t[`${alias}`] };
    });
  };
  return [comvertUserIdField(picDatas, 'PIC_ID'), comvertUserIdField(cneeDatas, 'CNEE_ID')];
};

function* watchCustomsClearanceRequest() {
  yield takeLatest(filterCustomsClearanceRequest, fetchCustomsClearanceRequest);
  yield takeLatest(getDetailCustClearReq, fetchCustomsClearanceRequestDetail);
  yield takeLatest(getPic, fetchPic);
  yield takeLatest(getCnee, fetchCnee);
  yield takeLatest(getShippingInstruction, fetchShippingInstruction);
  yield takeLatest(registerCustomsClearanceRequest, registerCustomsClearanceRequestSaga);
  yield takeLatest(updateCustomsClearanceRequest, updateCustomsClearanceRequestSaga);
  yield takeLatest(deleteCustomsClearanceRequest, deleteCustomsClearanceRequestSaga);
  yield takeLatest(getHoliday, fetchHoliday);
  yield takeLatest(uploadOutsideDoc, uploadOutsideDocSaga);
  yield takeLatest(filterCustomsClearanceRequestCalendar, fetchCustomsClearanceCalendarRequest);
  yield takeLatest(foldFlagHblDisplayCalendarFlg, fetchFoldFlagHblDisplayCalendarFlg);
}

export default watchCustomsClearanceRequest;
